import type { IconikCustomActionContext } from "./service";

/**
 * Maps `@cinesys/experience-ui`'s `Gear`/`GearMenuHost` `menuType` (a Cantemo-shaped UI-surface
 * label — see `components/GearMenu/Menu.tsx`'s own header comment) onto the iconik
 * `CustomActionSchema.context` enum a real entries fetch needs. Only the values this repo's wired
 * callers (`ItemTitle.tsx`, `ResultCard.tsx`, `ItemBrowserList.tsx`) ever pass are mapped; every
 * other `menuType` (e.g. `fileBrowse`/`jobs`) returns `undefined` — no iconik context maps to
 * those Cantemo storage/job UI-surface distinctions, so no fetch fires and that gear falls back to
 * its own static `gearOptions`, or an honest "No actions available".
 *
 * `itemFileBrowseLarge`/`searchItemFile` (`ItemBrowserList.tsx`'s bulk-selection gear) map to
 * `BULK`, not `ASSET`: iconik's `custom_actions` API has a distinct `BULK` context —
 * `CustomActionSchema.context`'s enum includes both `ASSET` and `BULK` as separate registrations,
 * though the trigger callback body shape is identical either way, `asset_ids: [string]`. An org
 * can register actions that only make sense against a genuine multi-select (e.g. a batch
 * transcode) under `BULK` and never see them offered on a single-item gear, so reusing `ASSET`
 * here would silently hide those.
 */
export function customActionContextForMenuType(
  menuType: string | undefined
): IconikCustomActionContext | undefined {
  switch (menuType) {
    case "itemBrowse":
      return "ASSET";
    // A Browse-page file row backed by a real iconik asset — same underlying entity as
    // `itemBrowse` above, just a different Cantemo UI-surface label.
    case "itemFileBrowse":
      return "ASSET";
    // The bulk-selection gear (`ItemBrowserList.tsx`'s `SelectBanner`), whether exactly one
    // asset-linked row is selected (`itemFileBrowseLarge`) or two-plus (`searchItemFile`) — both
    // go through iconik's own `BULK` custom-action context, not `ASSET`, per this function's own
    // header comment. `fileBrowseLarge`/`searchFile` (no `itemId` at all — plain, not-yet-imported
    // files) fall through to `undefined` below, same treatment `itemFileBrowse`'s sibling
    // `fileBrowse` already gets: a plain file isn't an iconik asset, so there's no context to ask
    // for.
    case "itemFileBrowseLarge":
    case "searchItemFile":
      return "BULK";
    case "subClip":
      return "ASSET_SUBCLIP";
    case "collection":
      return "COLLECTION";
    // A card representing a saved search itself (never a saved search's own CONTENTS, which are
    // plain assets and already resolve through "itemBrowse"/"subClip" above) — e.g. Widget.tsx's
    // home-page "Saved Searches" widget.
    case "savedSearch":
      return "SAVED_SEARCH";
    default:
      return undefined;
  }
}
